import { useState, useEffect, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  Search,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  Upload,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  DollarSign,
  Eye,
  Close,
  MoreVertical,
  RefreshCw,
  FileText,
  Activity,
  Wallet,
  CreditCard,
  ArrowLeft,
  SlidersHorizontal,
  Grid3X3,
  List,
  Trash2,
  AlertTriangle,
  PlusCircle,
  Timer
} from "lucide-react"
import { exchangeActions } from "../../../redux/actions"
import { useDispatch, useSelector } from "react-redux"
import { allRemoteSettingsSelect, exchangeSelect, homeScreenStatSelect } from "../../../redux/selectors"
import WrapUpP2P from "./layouts/WrapUpP2p"
import { formatDateTime, money } from "../../../utility/Helper1"
import notify from "../../../utility/NotificationSystem"

const TransactionHistoryPage = () => {
  // Enhanced mock data with more comprehensive transaction records
  const dispatch = useDispatch();
  const allTransactions = useSelector(exchangeSelect);
  // State management
  const [searchQuery, setSearchQuery] = useState("")
  const [filterType, setFilterType] = useState("all") // all, investment, withdrawal
  const [filterStatus, setFilterStatus] = useState("all") // all, pending, active, completed, matured
  const [sortBy, setSortBy] = useState("date") // date, amount, status
  const [sortOrder, setSortOrder] = useState("desc") // asc, desc
  const [viewMode, setViewMode] = useState("grid") // list, grid
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [openActionMenu, setOpenActionMenu] = useState(null)
  const finance = useSelector(homeScreenStatSelect)
  const navigate = useNavigate();
  const site_settings = useSelector(allRemoteSettingsSelect)

  // Custom countdown hook (replace with react-countdown in your actual implementation)
  const useCountdown = (targetDate) => {
    const [timeLeft, setTimeLeft] = useState(0)
    const [isCompleted, setIsCompleted] = useState(false)

    useEffect(() => {
      const calculateTimeLeft = () => {
        const now = Date.now()
        const difference = targetDate - now

        if (difference > 0) {
          setTimeLeft(difference)
          setIsCompleted(false)
        } else {
          setTimeLeft(0)
          setIsCompleted(true)
        }
      }

      calculateTimeLeft()
      const timer = setInterval(calculateTimeLeft, 1000)

      return () => clearInterval(timer)
    }, [targetDate])

    const formatTime = (milliseconds) => {
      const totalSeconds = Math.floor(milliseconds / 1000)
      const days = Math.floor(totalSeconds / (24 * 60 * 60))
      const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60))
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
      const seconds = totalSeconds % 60

      if (days > 0) {
        return `${days}d ${hours}h ${minutes}m ${seconds}s`
      } else if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`
      } else {
        return `${seconds}s`
      }
    }

    return {
      timeLeft: formatTime(timeLeft),
      isCompleted,
      rawTimeLeft: timeLeft
    }
  }

  const onLoadPH = () => { dispatch(exchangeActions.onGetExchange("p2p/load/all", response => { })) }
  useEffect(() => {
    onLoadPH()
  }, [])

  const getTimeAgo = (timestamp) => {
    const now = Date.now()
    const diffInSeconds = Math.floor((now - (timestamp * 1000)) / 1000)

    if (diffInSeconds < 60) return `${diffInSeconds}s ago`
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    return `${Math.floor(diffInSeconds / 86400)}d ago`
  }

  const getInvestmentStatus = (investment) => {
    if (investment.asset == 2) {
      return {
        status: investment.completed == 1 ? 'completed' : 'pending',
        label: investment.completed == 1 ? 'Completed' : 'Pending',
        color: investment.completed == 1 ? 'emerald' : 'amber'
      }
    }

    if (!investment.timephconf2 || investment.timephconf2 == 0) {
      return { status: 'pending', label: 'Pending', color: 'amber' }
    }

    if (investment.matchedph == 0) {
      return { status: 'unmatched', label: 'Unmatched', color: 'blue' }
    }

    const now = Date.now()
    const start = investment.timephconf2 * 1000
    const end = start + (investment.ghday * 24 * 60 * 60 * 1000)

    if (now >= end || investment.phready == "NO") {
      return { status: 'matured', label: 'Matured', color: 'emerald' }
    }

    if (investment.matchedph > 0) {
      return { status: 'active', label: 'Active', color: 'green' }
    }

    return { status: 'pending', label: 'Pending', color: 'amber' }
  }

  const getStatusColor = (color) => {
    const colors = {
      emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      green: 'bg-green-500/10 text-green-400 border-green-500/20',
      amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      red: 'bg-red-500/10 text-red-400 border-red-500/20',
      gray: 'bg-gray-500/10 text-gray-400 border-gray-500/20'
    }
    return colors[color] || colors.gray
  }

  // Export function
  const handleExport = () => {
    const csvData = filteredAndSortedTransactions.map(transaction => {
      const status = getInvestmentStatus(transaction)
      const isInvestment = transaction.asset == 1
      const amount = isInvestment ? transaction.lastph : transaction.lastgh

      return {
        'Transaction ID': transaction.transid,
        'Type': isInvestment ? 'Bid' : 'Withdrawal',
        'Description': transaction.description,
        'Amount': amount,
        'Status': status.label,
        'Date': formatDateTime(Number(transaction.phtimestamp2) * 1000),
        ...(isInvestment && {
          'Interest Rate': `${transaction.interestRate}%`,
          'Term (Days)': transaction.ghday,
          'Recommitted': transaction.recommitted == 1 ? 'Yes' : 'No'
        }),
        ...(!isInvestment && {
          'Method': transaction.method || 'N/A'
        })
      }
    })

    // Convert to CSV
    const csvHeaders = Object.keys(csvData[0] || {})
    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row =>
        csvHeaders.map(header => {
          const value = row[header] || ''
          // Escape quotes and wrap in quotes if contains comma
          return typeof value == 'string' && value.includes(',')
            ? `"${value.replace(/"/g, '""')}"`
            : value
        }).join(',')
      )
    ].join('\n')

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `transactions-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Enhanced filtering and sorting
  const filteredAndSortedTransactions = useMemo(() => {
    let filtered = allTransactions.filter(transaction => {
      // Search filter
      const matchesSearch = transaction.transid.toLowerCase().includes(searchQuery.toLowerCase()) ||
        transaction.description.toLowerCase().includes(searchQuery.toLowerCase())

      // Type filter
      const matchesType = filterType == "all" ||
        (filterType == "bid" && transaction.asset == 1) ||
        (filterType == "withdrawal" && transaction.asset == 2)

      // Status filter
      let matchesStatus = filterStatus == "all"
      if (!matchesStatus) {
        const status = getInvestmentStatus(transaction)
        matchesStatus = status.status == filterStatus
      }

      return matchesSearch && matchesType && matchesStatus
    })

    // Sorting
    filtered.sort((a, b) => {
      let aValue, bValue

      switch (sortBy) {
        case "amount":
          aValue = a.asset == 1 ? a.lastph : a.lastgh
          bValue = b.asset == 1 ? b.lastph : b.lastgh
          break
        case "status":
          aValue = getInvestmentStatus(a).status
          bValue = getInvestmentStatus(b).status
          break
        case "date":
        default:
          aValue = a.phtimestamp2
          bValue = b.phtimestamp2
          break
      }

      if (sortOrder == "asc") {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

    return filtered
  }, [allTransactions, searchQuery, filterType, filterStatus, sortBy, sortOrder])

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedTransactions.length / itemsPerPage)
  const paginatedTransactions = filteredAndSortedTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  // Statistics
  const stats = useMemo(() => {
    const investments = allTransactions.filter(t => t.asset == 1)
    const withdrawals = allTransactions.filter(t => t.asset == 2)

    return {
      totalInvestments: investments.length,
      totalWithdrawals: withdrawals.length,
      totalInvestmentAmount: investments.reduce((sum, inv) => sum + inv.lastph, 0),
      totalWithdrawalAmount: withdrawals.reduce((sum, wth) => sum + wth.lastgh, 0),
      pendingTransactions: allTransactions.filter(t => {
        const status = getInvestmentStatus(t)
        return status.status == 'pending'
      }).length
    }
  }, [allTransactions])

  const handleSort = (field) => {
    if (sortBy == field) {
      setSortOrder(sortOrder == "asc" ? "desc" : "asc")
    } else {
      setSortBy(field)
      setSortOrder("desc")
    }
  }

  // Action Menu Component
  const ActionMenu = ({ transaction }) => {
    const isInvestment = transaction.asset == 1
    const isOpen = openActionMenu == transaction.id

    return (
      <div className="relative">
        <button
          onClick={() => setOpenActionMenu(isOpen ? null : transaction.id)}
          className="p-2 hover:bg-slate-600/50 rounded-lg transition-colors duration-200"
        >
          <MoreVertical className="h-4 w-4 text-slate-400" />
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-slate-800 rounded-lg border border-slate-600/50 shadow-xl z-50">
            <div className="py-1">
              <button
                onClick={() => {
                  setOpenActionMenu(null)
                }}
                className="w-full px-4 py-2 text-left text-slate-300 hover:bg-slate-700/50 flex items-center space-x-2"
              >
                <XCircle className="h-4 w-4" />
                <span>Close</span>
              </button>

              {isInvestment && transaction.lastph == transaction.ph && (
                <button
                  onClick={() => {
                    setShowDeleteConfirm(transaction)
                    setOpenActionMenu(null)
                  }}
                  className="w-full px-4 py-2 text-left text-red-400 hover:bg-red-500/10 flex items-center space-x-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Delete Confirmation Modal
  const DeleteConfirmModal = ({ transaction }) => {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-slate-800 rounded-xl border border-slate-600/50 p-6 max-w-md w-full mx-4">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Delete Bid</h3>
          </div>

          <p className="text-slate-300 mb-6">
            Are you sure you want to delete bid <span className="font-medium text-amber-400">{transaction.transid}</span>?
            This action cannot be undone.
          </p>

          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={() => setShowDeleteConfirm(null)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={() => deletePH(transaction.id)}
              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors duration-200"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionMenu !== null) {
        setOpenActionMenu(null)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [openActionMenu])

  const deletePH = (objToDelete) => {
    dispatch(exchangeActions.onGetExchange(`p2p/exchange/delete/${objToDelete}`, response => {
      notify(response.msg, response.code)
      setShowDeleteConfirm(null)
    }))
  }

  const handleRecommit = (transaction) => {
    navigate(`/new/bid?recommit_for=${transaction.transid}`)
  }

  const TransactionCard = ({ transaction }) => {
    const status = getInvestmentStatus(transaction)
    const isInvestment = transaction.asset == 1
    const amount = isInvestment ? transaction.lastph : transaction.lastgh
    const remain = isInvestment ? transaction.ph : transaction.activewallet
    const createdOn = isInvestment ? transaction.phtimestamp2 : transaction.ghtimestamp2
    const dateOfGH = (Number(transaction.timephconf2) * 1000) + (Number(transaction.ghday) * 24 * 60 * 60 * 1000)
    const minimumHourBeforeGH = Number(site_settings?.operational_settings?.p2p_recommitment_minimum_hour_before_gh) * 60 * 60 * 1000
    const minimumRecommitmentTimestamp = dateOfGH - minimumHourBeforeGH
    const shouldRecommitOnEach = site_settings?.operational_settings?.p2p_recommitment_for_each == 1

    // Countdown hook
    const countdown = useCountdown(minimumRecommitmentTimestamp)

    // Check if recommit conditions are met
    const canShowRecommit = shouldRecommitOnEach &&
      isInvestment &&
      transaction.phready == "NO" &&
      transaction.recommitted == 0
    const isRecomitting = (transaction?.isRecomitting ?? '') != ''

    return (
      <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/30 backdrop-blur-sm rounded-xl border border-slate-600/30 hover:border-amber-500/30 transition-all duration-300 p-6 group">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-xl ${isInvestment ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30' : 'bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border border-amber-500/30'}`}>
              {isInvestment ? <TrendingUp className="h-5 w-5 text-emerald-400" /> : <Download className="h-5 w-5 text-amber-400" />}
            </div>
            <div>
              <h3 className="text-white font-medium text-lg">{transaction.transid}</h3>
              <p className="text-slate-400 text-sm">{formatDateTime(Number(createdOn) * 1000)}</p>

              <div className="flex items-start space-x-3">
                <div className="text-right">
                  <p className={`text-xl font-bold ${isInvestment ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {isInvestment ? '-' : '+'}{money(amount)}
                    {remain != 0 && <span> / <span className="text-slate-400 text-sm">{money(remain)} (uncleared)</span></span>}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <ActionMenu transaction={transaction} />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(status.color)}`}>
            {status.label}
          </span>
        </div>

        {/* Recommit Section */}
        {canShowRecommit && (
          <div className="mt-4 pt-4 border-t border-slate-600/30">
            <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Timer className="h-4 w-4 text-blue-400" />
                {!isRecomitting && <span className="text-blue-400 font-medium text-sm">Recommitment Available</span>}
                {isRecomitting && <span className="text-green-400 font-medium text-sm">Pending Recommitment Action Detected</span>}
              </div>

              {!isRecomitting && <>
                {!countdown.isCompleted ? (
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm">Time until recommit available:</p>
                    <div className="bg-slate-700/50 rounded-lg p-2">
                      <span className="text-amber-400 font-mono text-sm">{countdown.timeLeft}</span>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => handleRecommit(transaction)}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium"
                  >
                    Recommit Now
                  </button>
                )}
              </>}
            </div>
          </div>
        )}

        {isInvestment && (
          <div className="mt-4 pt-4 border-t border-slate-600/30">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-400">Term:</span>
                <span className="text-white ml-2">{transaction.ghday} days</span>
              </div>
              {transaction?.timephconf2 != 0 && <div>
                <span className="text-slate-400">Maturity Date:</span>
                <span className="text-emerald-400 ml-2">{formatDateTime(dateOfGH)}</span>
              </div>}
            </div>
          </div>
        )}
      </div>
    )
  }

  const TransactionRow = ({ transaction }) => {
    const status = getInvestmentStatus(transaction)
    const isInvestment = transaction.asset == 1
    const amount = isInvestment ? transaction.lastph : transaction.lastgh
    const createdOn = isInvestment ? transaction.phtimestamp2 : transaction.ghtimestamp2
    const dateOfGH = (Number(transaction.timephconf2) * 1000) + (Number(transaction.ghday) * 24 * 60 * 60 * 1000)
    const minimumHourBeforeGH = Number(site_settings?.operational_settings?.p2p_recommitment_minimum_hour_before_gh) * 60 * 60 * 1000
    const minimumRecommitmentTimestamp = dateOfGH - minimumHourBeforeGH
    const shouldRecommitOnEach = site_settings?.operational_settings?.p2p_recommitment_for_each == 1

    // Countdown hook
    const countdown = useCountdown(minimumRecommitmentTimestamp)

    // Check if recommit conditions are met
    const canShowRecommit = shouldRecommitOnEach &&
      isInvestment &&
      transaction.asset == 1 &&
      transaction.phready === "NO" &&
      transaction.recommitted == 0

    return (
      <>
        <tr className="hover:bg-slate-700/20 transition-colors duration-200 border-b border-slate-700/30">
          <td className="px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${isInvestment ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-amber-500/20 border border-amber-500/30'}`}>
                {isInvestment ? <TrendingUp className="h-4 w-4 text-emerald-400" /> : <Download className="h-4 w-4 text-amber-400" />}
              </div>
              <div>
                <div className="text-white font-medium">{transaction.transid}</div>
                <div className="text-slate-400 text-sm">{formatDateTime(Number(createdOn) * 1000)}</div>
              </div>
            </div>
          </td>
          <td className="px-6 py-4">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${isInvestment ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
              {isInvestment ? 'Bid' : 'Withdrawal'}
            </span>
          </td>
          <td className="px-6 py-4">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(status.color)}`}>
              {status.label}
            </span>
          </td>
          <td className="px-6 py-4">
            <div className={`text-lg font-bold ${isInvestment ? 'text-emerald-400' : 'text-amber-400'}`}>
              {isInvestment ? '-' : '+'}{money(amount)}
            </div>
          </td>
          <td className="px-6 py-4">
            <div className="text-slate-400 text-sm">{getTimeAgo(createdOn)}</div>
          </td>
          <td className="px-6 py-4">
            <div className="flex items-center space-x-2">
              {canShowRecommit && (
                <div className="mr-2">
                  {!countdown.isCompleted ? (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg px-2 py-1">
                      <div className="flex items-center space-x-1">
                        <Timer className="h-3 w-3 text-blue-400" />
                        <span className="text-blue-400 text-xs font-mono">{countdown.timeLeft}</span>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleRecommit(transaction)}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white px-3 py-1 rounded-lg transition-all duration-200 text-xs font-medium"
                    >
                      Recommit
                    </button>
                  )}
                </div>
              )}
              <ActionMenu transaction={transaction} />
            </div>
          </td>
        </tr>
      </>
    )
  }

  return (<WrapUpP2P allowAutoPadding={false}>
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmModal transaction={showDeleteConfirm} />
      )}

      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">
                  Transaction History
                </h1>
                <p className="text-slate-400 mt-1">Complete overview</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleExport}
                className="flex items-center space-x-2 bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 px-4 py-2 rounded-lg border border-slate-600/50 transition-all duration-200"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
              <button onClick={() => navigate('/new/bid')} className="flex items-center space-x-2 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 px-4 py-2 rounded-lg transition-all duration-200">
                <PlusCircle className="h-4 w-4" />
                <span>Bid Now</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 backdrop-blur-sm rounded-xl border border-emerald-500/20 p-6">
            <div className="flex items-center space-x-3 mb-2">
              <TrendingUp className="h-5 w-5 text-emerald-400" />
              <span className="text-slate-400 text-sm">Investments</span>
            </div>
            <div className="text-2xl font-bold text-emerald-400">{stats.totalInvestments}</div>
            <div className="text-sm text-slate-400 mt-1">{money(stats.totalInvestmentAmount)}</div>
          </div>

          <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 backdrop-blur-sm rounded-xl border border-amber-500/20 p-6">
            <div className="flex items-center space-x-3 mb-2">
              <Download className="h-5 w-5 text-amber-400" />
              <span className="text-slate-400 text-sm">Withdrawals</span>
            </div>
            <div className="text-2xl font-bold text-amber-400">{stats.totalWithdrawals}</div>
            <div className="text-sm text-slate-400 mt-1">{money(stats.totalWithdrawalAmount)}</div>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/30 backdrop-blur-sm rounded-xl border border-slate-600/30 p-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all duration-200"
              />
            </div>

            {/* Type Filter */}
            <div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all duration-200"
              >
                <option value="all">All Types</option>
                <option value="bid">Bid</option>
                <option value="withdrawal">Withdrawals</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all duration-200"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="matured">Matured</option>
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2">
              <div className="flex bg-slate-700/50 rounded-lg p-1 border border-slate-600/50">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-md transition-all duration-200 ${viewMode == "grid"
                    ? "bg-amber-500/20 text-amber-400"
                    : "text-slate-400 hover:text-white"
                    }`}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-md transition-all duration-200 ${viewMode == "list"
                    ? "bg-amber-500/20 text-amber-400"
                    : "text-slate-400 hover:text-white"
                    }`}
                >
                  <List className="h-4 w-4" />
                </button>

              </div>
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-600/30">
            <div className="flex items-center space-x-4">
              <span className="text-slate-400 text-sm">Sort by:</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleSort("date")}
                  className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-all duration-200 ${sortBy == "date"
                    ? "bg-amber-500/20 text-amber-400"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                    }`}
                >
                  <Calendar className="h-3 w-3" />
                  <span>Date</span>
                  {sortBy == "date" && (
                    sortOrder == "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                  )}
                </button>
                <button
                  onClick={() => handleSort("amount")}
                  className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-all duration-200 ${sortBy == "amount"
                    ? "bg-amber-500/20 text-amber-400"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                    }`}
                >
                  <DollarSign className="h-3 w-3" />
                  <span>Amount</span>
                  {sortBy == "amount" && (
                    sortOrder == "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                  )}
                </button>
                <button
                  onClick={() => handleSort("status")}
                  className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-all duration-200 ${sortBy == "status"
                    ? "bg-amber-500/20 text-amber-400"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                    }`}
                >
                  <Activity className="h-3 w-3" />
                  <span>Status</span>
                  {sortBy == "status" && (
                    sortOrder == "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>

            <div className="text-slate-400 text-sm">
              Showing {paginatedTransactions.length} of {filteredAndSortedTransactions.length} transactions
            </div>
          </div>
        </div>

        {/* Transactions Display */}
        {viewMode == "grid" ? (
          /* Grid View */
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
            {paginatedTransactions.map((transaction) => (
              <TransactionCard key={transaction.id} transaction={transaction} />
            ))}
          </div>
        ) : (
          /* List View */
          <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/30 backdrop-blur-sm rounded-xl border border-slate-600/30 overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Transaction
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map((transaction) => (
                    <TransactionRow key={transaction.id} transaction={transaction} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredAndSortedTransactions.length == 0 && (
          <div className="text-center py-16">
            <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/30 backdrop-blur-sm rounded-xl border border-slate-600/30 p-12">
              <FileText className="h-16 w-16 text-slate-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">No transactions found</h3>
              <p className="text-slate-400 mb-6">
                {searchQuery || filterType !== "all" || filterStatus !== "all"
                  ? "Try adjusting your filters or search terms"
                  : "You haven't made any transactions yet"}
              </p>
              <button
                onClick={() => {
                  setSearchQuery("")
                  setFilterType("all")
                  setFilterStatus("all")
                }}
                className="bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 px-6 py-3 rounded-lg transition-all duration-200"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between bg-gradient-to-r from-slate-800/50 to-slate-700/30 backdrop-blur-sm rounded-xl border border-slate-600/30 px-6 py-4">
            <div className="text-slate-400 text-sm">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage == 1}
                className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600/50 transition-all duration-200"
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 ${currentPage == pageNum
                        ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                        : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                        }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage == totalPages}
                className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600/50 transition-all duration-200"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  </WrapUpP2P>
  )
}

export default TransactionHistoryPage